import submit from "submit-form";
import getAvatarUrl from "../../utils/get-avatar-url";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import SvgIcon from "../SvgIcon";
import useClickOutside from "../../hooks/useClickOutside";
import { getCoachFromAllAthletes } from "../../utils/user-helpers";

export const AthleteSelect = (
  {
    athleteSwitch = false,
    athleteList,
    handleAthleteChange,
    name, //the input name if an input with the athlete id is needed
    initialAthlete,
    classNames,
  },

  { locals }
) => {
  const { activeUser, organizationRoleName, allAthletesFromOrganization } =
    locals || {};
  const referAsClients = activeUser.referAsClients || false;
  const theme = activeUser.webAppTheme || "dark";

  //The user is allowed to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsCoaches =
    organizationRoleName === "Owner" ||
    organizationRoleName === "Admin" ||
    organizationRoleName === "Nutritionist";

  const athletes =
    (athleteList && sortAthletes(athleteList)) ||
    (isAllowToViewAllOrganizationsCoaches &&
      sortAthletes(allAthletesFromOrganization)) ||
    sortAthletes(activeUser?.athletes) ||
    [];

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [searchAthlete, setSearchAthlete] = useState("");
  const [filteredList, setFilteredList] = useState([]);
  const [searchOpened, setSearchOpened] = useState(false);
  const [athleteData, setAthleteData] = useState({
    name: "",
    imgSrc: "",
    id: "",
  });
  useClickOutside(dropdownRef, () => setSearchOpened(false), searchOpened);
  useEffect(() => {
    searchAthlete.length > 0
      ? setFilteredList(filterAthletes(searchAthlete, athletes))
      : setFilteredList(athletes);
  }, [searchAthlete]);
  useEffect(() => {
    if (locals.query.athlete) return setSelectedAthlete(locals.query.athlete);
    if (initialAthlete) return setSelectedAthlete(initialAthlete);
  }, [locals.query.athlete, initialAthlete]);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);

  const setSelectedAthlete = (athleteId) => {
    const athlete = athletes.find(
      (athlete) => athlete._id === athleteId || athlete.athleteId === athleteId
    );

    setAthleteData({
      name: athlete?.name || athlete?.athleteName || "",
      imgSrc: getAvatarUrl(athlete, null, theme),
      id: athleteId,
    });
  };
  function filterAthletes(searchString, athletes) {
    const filteredAthletes = athletes.filter((athlete) => {
      const nameVariable = athlete.name ? "name" : "athleteName";
      return athlete[nameVariable]
        .toLowerCase()
        .includes(searchString.trim().toLowerCase());
    });
    return filteredAthletes;
  }
  const updateAthlete = (id) => {
    if (handleAthleteChange) {
      setSelectedAthlete(id);
      setSearchOpened(false);
      return handleAthleteChange(id);
    }
    const { isFromOtherOrganizationMember, organizationCoachId } =
      getCoachFromAllAthletes(activeUser, id, allAthletesFromOrganization);
    let body = { athlete: id, athleteSwitch };
    if (isFromOtherOrganizationMember) {
      body.organizationCoach = organizationCoachId;
    } else {
      body.organizationCoach = "";
    }
    if (locals.pathname.includes("metrics/check-in-summary")) {
      body = { ...body, checkInDate: "", checkInFrequency: "" };
    }
    submit(`/actions/refresh`, {
      method: "GET",
      body,
    });
    setSearchAthlete("");
    setSearchOpened(false);
  };
  const handleClick = (e) => {
    e.preventDefault();
    if (!searchOpened) {
      setSearchOpened(true);
      focusInput();
    }
  };
  const focusInput = () => {
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 500);
  };
  const handleKeyPress = (event) => {
    const { key } = event;

    if (searchOpened) {
      switch (key) {
        case "Escape":
          setSearchOpened(false);
          setSearchAthlete("");
          break;
        case "ArrowDown":
          moveFocusDown();
          break;
        case "ArrowUp":
          moveFocusUp();
          break;
        case "Enter":
          const athleteId = filteredList[currentOptionIndex]?.athleteId;
          if (athleteId) {
            setSelectedAthlete(athleteId);
            updateAthlete(athleteId);
          }
          break;
        default:
          break;
      }
    }
  };

  const moveFocusDown = () => {
    if (currentOptionIndex < filteredList.length - 1) {
      setCurrentOptionIndex(currentOptionIndex + 1);
    } else {
      setCurrentOptionIndex(0);
    }
  };

  const moveFocusUp = () => {
    if (currentOptionIndex > 0) {
      setCurrentOptionIndex(currentOptionIndex - 1);
    } else {
      setCurrentOptionIndex(filteredList.length - 1);
    }
  };
  return (
    <div className={classNames}>
      {name && <input name={name} type="hidden" value={athleteData.id} />}
      <details role="list" open className="dropdown-img" ref={dropdownRef}>
        <summary onClick={handleClick}>
          {athleteData.imgSrc.length > 0 && <img src={athleteData.imgSrc} />}
          {searchOpened ? (
            <input
              ref={inputRef}
              value={searchAthlete}
              onChange={(e) => setSearchAthlete(e.target.value)}
              onKeyDown={(e) => {
                if (["Enter", "Tab"].includes(e.key)) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                handleKeyPress(e);
              }}
            />
          ) : (
            <p
              className={
                athleteData.name
                  ? undefined
                  : "text-[--white-30] flex items-center gap-2 no-athlete-selected"
              }
            >
              {athleteData.name ||
                `Search ${referAsClients ? "client" : "athlete"}`}
            </p>
          )}
          <SvgIcon
            src={
              searchOpened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"
            }
          />
        </summary>
        <ul role="listbox" className={searchOpened ? "opened" : ""}>
          {filteredList.map((athlete, i) => (
            <li
              key={athlete._id || athlete.athleteId}
              onClick={() => updateAthlete(athlete._id || athlete.athleteId)}
              role="option"
              className={currentOptionIndex === i ? "current" : ""}
            >
              <img src={getAvatarUrl(athlete, _, theme)} />
              <span>{athlete.name || athlete.athleteName}</span>
            </li>
          ))}
          {filteredList.length === 0 && searchAthlete.length > 0 && (
            <li>No results found</li>
          )}
        </ul>
      </details>
    </div>
  );
};

AthleteSelect.contextTypes = { locals: PropTypes.object };

const sortAthletes = (athletes) => {
  if (athletes) {
    // Sort athletes by athleteName
    athletes.sort((athleteA, athleteB) => {
      const nameVariable = athleteA.name ? "name" : "athleteName";
      const nameA = athleteA[nameVariable].toUpperCase(); // ignore upper and lowercase
      const nameB = athleteB[nameVariable].toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0; // names must be equal
    });

    return athletes;
  }
  return false;
};
